import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, Eye, Printer, Trash2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate, formatCurrency } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg";
const COMPANY_NAME = "Wilyrash Farm";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const { currencySymbol } = useCurrency();
  const invoicePrintRef = useRef(null);
  
  // Get current user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || '';
  const isAdmin = userRole === 'admin';
  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    tax: '0',
    status: 'unpaid',
    items: [{ description: 'Eggs', quantity: '', unit_price: '' }]
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, getAuthHeader());
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/list`, getAuthHeader());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, formData, getAuthHeader());
      toast.success('Invoice created successfully');
      fetchInvoices();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${newStatus}`, {}, getAuthHeader());
      toast.success('Invoice status updated');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`, getAuthHeader());
      setViewInvoice(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      customer_id: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      tax: '0',
      status: 'unpaid',
      items: [{ description: 'Eggs', quantity: '', unit_price: '' }]
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: '', unit_price: '' }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: 'badge-success',
      unpaid: 'badge-warning',
      overdue: 'badge-error',
      cancelled: 'badge-neutral'
    };
    return badges[status] || 'badge-neutral';
  };

  // Print invoice function
  const handlePrintInvoice = () => {
    if (!viewInvoice) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${viewInvoice.invoice_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
            .company-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-info { display: flex; align-items: center; gap: 15px; }
            .company-logo { width: 70px; height: 70px; border-radius: 50%; object-fit: cover; }
            .company-name { font-size: 22px; font-weight: bold; color: #10b981; }
            .company-tagline { font-size: 11px; color: #666; }
            .invoice-title { text-align: right; }
            .invoice-title h1 { font-size: 28px; color: #333; letter-spacing: 2px; }
            .invoice-number { font-size: 14px; color: #666; margin-top: 5px; }
            .invoice-number span { font-family: monospace; font-weight: bold; color: #10b981; }
            .addresses { display: flex; justify-content: space-between; margin-bottom: 30px; }
            .address-block { max-width: 45%; }
            .address-block h3 { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
            .address-block p { font-size: 13px; line-height: 1.6; }
            .address-block .name { font-weight: 600; font-size: 14px; }
            .dates-status { display: flex; gap: 30px; margin-bottom: 30px; padding: 15px; background: #f8fafc; border-radius: 8px; }
            .date-item { flex: 1; }
            .date-item label { font-size: 11px; color: #666; text-transform: uppercase; display: block; margin-bottom: 3px; }
            .date-item span { font-weight: 600; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
            .status-paid { background: #d1fae5; color: #059669; }
            .status-unpaid { background: #fef3c7; color: #d97706; }
            .status-overdue { background: #fee2e2; color: #dc2626; }
            .status-cancelled { background: #e5e7eb; color: #6b7280; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            .items-table th { background: #10b981; color: white; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; }
            .items-table th:last-child, .items-table td:last-child { text-align: right; }
            .items-table td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
            .items-table tbody tr:hover { background: #f8fafc; }
            .totals { margin-left: auto; width: 280px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; }
            .totals-row.subtotal { border-bottom: 1px solid #e2e8f0; }
            .totals-row.grand-total { border-top: 2px solid #10b981; margin-top: 10px; padding-top: 15px; font-size: 18px; font-weight: bold; color: #10b981; }
            .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; }
            .footer p { font-size: 11px; color: #666; }
            .footer .thank-you { font-size: 14px; font-weight: 600; color: #10b981; margin-bottom: 10px; }
            @media print {
              body { padding: 15px; }
              .invoice-container { max-width: 100%; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="company-header">
              <div class="company-info">
                <img src="${COMPANY_LOGO}" alt="Logo" class="company-logo" />
                <div>
                  <div class="company-name">${COMPANY_NAME}</div>
                  <div class="company-tagline">Poultry Farm Management</div>
                </div>
              </div>
              <div class="invoice-title">
                <h1>INVOICE</h1>
                <div class="invoice-number">Invoice No: <span>${viewInvoice.invoice_number}</span></div>
              </div>
            </div>
            
            <div class="addresses">
              <div class="address-block">
                <h3>From</h3>
                <p class="name">${COMPANY_NAME}</p>
                <p>Poultry Farm<br/>Nigeria</p>
              </div>
              <div class="address-block">
                <h3>Bill To</h3>
                <p class="name">${viewInvoice.customer_name}</p>
                <p>${viewInvoice.customer_phone || ''}<br/>${viewInvoice.customer_email || ''}<br/>${viewInvoice.customer_address || ''}</p>
              </div>
            </div>
            
            <div class="dates-status">
              <div class="date-item">
                <label>Invoice Date</label>
                <span>${viewInvoice.invoice_date}</span>
              </div>
              <div class="date-item">
                <label>Due Date</label>
                <span>${viewInvoice.due_date}</span>
              </div>
              <div class="date-item">
                <label>Status</label>
                <span class="status-badge status-${viewInvoice.status}">${viewInvoice.status}</span>
              </div>
            </div>
            
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width: 50%">Description</th>
                  <th style="width: 15%">Qty</th>
                  <th style="width: 17.5%">Unit Price</th>
                  <th style="width: 17.5%">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${viewInvoice.items.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td>${item.quantity}</td>
                    <td>${currencySymbol}${formatCurrency(item.unit_price)}</td>
                    <td>${currencySymbol}${formatCurrency(item.quantity * item.unit_price)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="totals">
              <div class="totals-row subtotal">
                <span>Subtotal</span>
                <span>${currencySymbol}${formatCurrency(viewInvoice.subtotal)}</span>
              </div>
              <div class="totals-row">
                <span>Tax</span>
                <span>${currencySymbol}${formatCurrency(viewInvoice.tax)}</span>
              </div>
              <div class="totals-row grand-total">
                <span>Total</span>
                <span>${currencySymbol}${formatCurrency(viewInvoice.total_amount)}</span>
              </div>
            </div>
            
            <div class="footer">
              <p class="thank-you">Thank you for your business!</p>
              <p>Generated by ${COMPANY_NAME} Billing System</p>
              <p>This is a computer-generated document. No signature required.</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Delete invoice function (Admin only)
  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, getAuthHeader());
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Invoices
          </h1>
          <p className="text-lg text-slate-600 mt-2">Generate and manage customer invoices</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-invoice-button" className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="customer">Customer</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, customer_id: value })} value={formData.customer_id}>
                  <SelectTrigger data-testid="invoice-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice_date">Invoice Date</Label>
                  <Input
                    id="invoice_date"
                    type="date"
                    data-testid="invoice-date-input"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    data-testid="invoice-due-date-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label>Items</Label>
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <Input
                      placeholder="Description"
                      data-testid={`item-description-${index}`}
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      data-testid={`item-quantity-${index}`}
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      required
                      min="0"
                      className="w-24"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      data-testid={`item-price-${index}`}
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      required
                      min="0"
                      className="w-24"
                    />
                    {formData.items.length > 1 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => removeItem(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2">
                  Add Item
                </Button>
              </div>

              <div>
                <Label htmlFor="tax">Tax ({currencySymbol})</Label>
                <Input
                  id="tax"
                  type="number"
                  step="0.01"
                  data-testid="invoice-tax-input"
                  value={formData.tax}
                  onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                  min="0"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" data-testid="invoice-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Create Invoice
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th className="text-right">Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="invoices-table-body">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="font-mono font-semibold">{invoice.invoice_number}</td>
                  <td>{invoice.customer_name}</td>
                  <td>{formatDate(invoice.invoice_date)}</td>
                  <td>{formatDate(invoice.due_date)}</td>
                  <td className="text-right font-semibold">{currencySymbol}{formatCurrency(invoice.total_amount)}</td>
                  <td>
                    <Select onValueChange={(value) => handleUpdateStatus(invoice.id, value)} value={invoice.status}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unpaid">Unpaid</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`view-invoice-${invoice.id}`}
                        onClick={() => handleViewInvoice(invoice.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="View Invoice"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          data-testid={`delete-invoice-${invoice.id}`}
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Invoice"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No invoices yet. Create your first invoice!
            </div>
          )}
        </div>
      </div>

      {/* Invoice View Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Invoice Details</span>
              {viewInvoice && (
                <Button
                  data-testid="print-invoice-button"
                  onClick={handlePrintInvoice}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4" ref={invoicePrintRef}>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Invoice Number</p>
                  <p className="font-mono font-semibold">{viewInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <span className={getStatusBadge(viewInvoice.status)}>{viewInvoice.status}</span>
                </div>
                <div>
                  <p className="text-slate-500">Customer</p>
                  <p className="font-semibold">{viewInvoice.customer_name}</p>
                  <p className="text-xs text-slate-600">{viewInvoice.customer_phone}</p>
                  {viewInvoice.customer_email && <p className="text-xs text-slate-600">{viewInvoice.customer_email}</p>}
                </div>
                <div>
                  <p className="text-slate-500">Dates</p>
                  <p>Invoice: {viewInvoice.invoice_date}</p>
                  <p>Due: {viewInvoice.due_date}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Items</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Qty</th>
                      <th className="text-right py-2">Price</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.items.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2">{item.description || '-'}</td>
                        <td className="text-right">{item.quantity || 0}</td>
                        <td className="text-right">{currencySymbol}{formatCurrency(item.unit_price || 0)}</td>
                        <td className="text-right">{currencySymbol}{formatCurrency((item.quantity || 0) * (item.unit_price || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-right space-y-1">
                <p>Subtotal: <span className="font-semibold">{currencySymbol}{formatCurrency(viewInvoice.subtotal || 0)}</span></p>
                <p>Tax: <span className="font-semibold">{currencySymbol}{formatCurrency(viewInvoice.tax || 0)}</span></p>
                <p className="text-lg font-bold">Total: {currencySymbol}{formatCurrency(viewInvoice.total_amount || 0)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;